import React, { createContext, useContext } from 'react';
export * from './contracts/permissions.js';
export * from './errors.js';
export * from './bus/MeshEventBus.js';
export interface Session {
  currentDid: string;
  displayName?: string;
  email?: string;
  role: 'mesh_owner' | 'member';
  sessionToken?: string;
}

export interface QueuedWallAction {
  actionId: string;
  type: 'POST' | 'LIKE' | 'UNLIKE' | 'FOLLOW' | 'UNFOLLOW';
  payload: Record<string, unknown>;
  createdAt: number;
  targetDid?: string;
}

export interface DtnBundlePayload {
  bundleId: string;
  sourceDid: string;
  targetDid?: string;
  actions: QueuedWallAction[];
  hops: number;
  timestamp: number;
}

export interface HostChatSlotProps {
  channelId: string;
  header?: React.ReactNode;
  sidebar?: React.ReactNode;
  beforeComposer?: React.ReactNode;
  messageAccessory?: (message: unknown) => React.ReactNode;
  className?: string;
  [key: string]: unknown;
}

export interface HostContextValue {
  session: Session;
  getApiBaseUrl: () => string;
  onNavigate?: (route: string) => void;
  ChatSlot?: React.ComponentType<HostChatSlotProps>;
  storage?: {
    getItem: (key: string) => Promise<any>;
    setItem: (key: string, val: any) => Promise<void>;
  };
  dtn?: {
    publish: (topic: string, payload: any) => Promise<void>;
  };
  events?: import('./bus/MeshEventBus.js').MeshEventBus;
}

export const HostContext = createContext<HostContextValue | null>(null);

export interface HostContextProviderProps {
  session: Session;
  apiBaseUrl?: string;
  onNavigate?: (route: string) => void;
  ChatSlot?: React.ComponentType<HostChatSlotProps>;
  events?: import('./bus/MeshEventBus.js').MeshEventBus;
  children: React.ReactNode;
}

export const HostContextProvider: React.FC<HostContextProviderProps> = ({
  session,
  apiBaseUrl = '',
  onNavigate,
  ChatSlot,
  events,
  children,
}) => {
  const value: HostContextValue = {
    session,
    getApiBaseUrl: () => apiBaseUrl || (typeof window !== 'undefined' ? window.location.origin : ''),
    onNavigate,
    ChatSlot,
    events,
  };

  return React.createElement(HostContext.Provider, { value }, children);
};

export const HostChatSlot: React.FC<HostChatSlotProps> = (props) => {
  const ctx = useContext(HostContext);
  if (ctx?.ChatSlot) {
    return React.createElement(ctx.ChatSlot, props);
  }
  return React.createElement(
    'div',
    {
      className: `herx-chat-slot-fallback ${props.className || ''}`,
      style: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        width: '100%',
        backgroundColor: '#0b0f19',
        color: '#64748b',
        fontSize: '13px',
      },
    },
    `Chat feed unavailable (${props.channelId})`
  );
};

export function useHostContext(): HostContextValue {
  const ctx = useContext(HostContext);
  if (!ctx) {
    throw new Error('[HerxSDK] useHostContext must be used within a HostContextProvider');
  }
  return ctx;
}

export async function httpJson<T>(url: string, init: RequestInit = {}): Promise<T> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10_000);

  const headers = new Headers(init.headers || {});
  if (!headers.has('Accept')) {
    headers.set('Accept', 'application/json');
  }
  if (!headers.has('Content-Type') && init.body && typeof init.body === 'string') {
    headers.set('Content-Type', 'application/json');
  }

  try {
    const res = await fetch(url, {
      ...init,
      headers,
      signal: controller.signal,
    });

    if (!res.ok) {
      const errText = await res.text().catch(() => '');
      throw new Error(`HTTP ${res.status} ${res.statusText}: ${errText}`);
    }

    return (await res.json()) as T;
  } finally {
    clearTimeout(timeoutId);
  }
}
